/**
 * editorStatePreserver.js
 * 
 * Saves and restores editor state (resume/cover letter) to sessionStorage
 * so users don't lose their work when redirected to pricing page.
 * 
 * Flow:
 * 1. User is editing resume/cover letter
 * 2. User tries to download → gets redirected to pricing
 * 3. BEFORE redirect: save all editor state to sessionStorage
 * 4. User completes payment → redirected back to editor
 * 5. ON MOUNT: restore state from sessionStorage, skip the "no template" redirect
 * 6. Editor state is fully restored, export dialog opens automatically
 */

const RESUME_EDITOR_STATE_KEY = 'pendingResumeEditorState';
const COVER_LETTER_EDITOR_STATE_KEY = 'pendingCoverLetterEditorState';

/**
 * Save resume editor state before navigating to pricing
 */
export function saveResumeEditorState({
  resumeData,
  templateHTML,
  selectedTemplate,
  colorTheme,
  fontFamily,
  hiddenSections,
  currentStep,
  photoPreview,
  resumeId,
  previewHTML,
}) {
  try {
    // IMPORTANT: Do NOT save photoPreview (base64 data URI — can be 1-5MB)
    // or previewHTML (rendered HTML — 50-150KB) to sessionStorage.
    // These large strings can exceed the 5MB sessionStorage quota, causing
    // the entire save to fail silently with QuotaExceededError.
    // Instead, save only the photo URL (small string) and re-render preview on restore.
    const state = {
      resumeData,
      templateHTML,
      selectedTemplate,
      colorTheme,
      fontFamily,
      hiddenSections,
      currentStep,
      photoPreview: '', // Excluded — too large (base64). Photo is in resumeData.photoURL.
      resumeId,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(RESUME_EDITOR_STATE_KEY, JSON.stringify(state));
    console.log('💾 Resume editor state saved to sessionStorage');
  } catch (err) {
    // If still too large (very long template), try without templateHTML as last resort
    console.error('❌ Failed to save resume editor state:', err);
    try {
      const fallbackState = {
        resumeData,
        templateHTML: '', // Will re-fetch from template service on restore
        selectedTemplate,
        colorTheme,
        fontFamily,
        hiddenSections,
        currentStep,
        photoPreview: '',
        resumeId,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(RESUME_EDITOR_STATE_KEY, JSON.stringify(fallbackState));
      console.log('💾 Resume editor state saved (without templateHTML) to sessionStorage');
    } catch (err2) {
      console.error('❌ Fallback save also failed:', err2);
    }
  }
}

/**
 * Restore resume editor state from sessionStorage (if exists)
 * Returns null if no saved state or state is expired (30 minutes)
 */
export function restoreResumeEditorState() {
  try {
    const raw = sessionStorage.getItem(RESUME_EDITOR_STATE_KEY);
    if (!raw) return null;

    const state = JSON.parse(raw);
    
    // Expire after 30 minutes to prevent stale state restoration
    const THIRTY_MINUTES = 30 * 60 * 1000;
    if (Date.now() - state.savedAt > THIRTY_MINUTES) {
      sessionStorage.removeItem(RESUME_EDITOR_STATE_KEY);
      console.log('⏰ Resume editor state expired, clearing');
      return null;
    }

    console.log('✅ Resume editor state restored from sessionStorage');
    return state;
  } catch (err) {
    console.error('❌ Failed to restore resume editor state:', err);
    return null;
  }
}

/**
 * Clear saved resume editor state (call after successful restore)
 */
export function clearResumeEditorState() {
  sessionStorage.removeItem(RESUME_EDITOR_STATE_KEY);
}

/**
 * Save cover letter editor state before navigating to pricing
 */
export function saveCoverLetterEditorState({
  formData,
  templateHTML,
  selectedTemplate,
  colorTheme,
  fontFamily,
}) {
  try {
    const state = {
      formData,
      templateHTML,
      selectedTemplate,
      colorTheme,
      fontFamily,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(COVER_LETTER_EDITOR_STATE_KEY, JSON.stringify(state));
    console.log('💾 Cover letter editor state saved to sessionStorage');
  } catch (err) {
    console.error('❌ Failed to save cover letter editor state:', err);
  }
}

/**
 * Restore cover letter editor state from sessionStorage (if exists)
 * Returns null if no saved state or state is expired (30 minutes)
 */
export function restoreCoverLetterEditorState() {
  try {
    const raw = sessionStorage.getItem(COVER_LETTER_EDITOR_STATE_KEY);
    if (!raw) return null;

    const state = JSON.parse(raw);
    
    const THIRTY_MINUTES = 30 * 60 * 1000;
    if (Date.now() - state.savedAt > THIRTY_MINUTES) {
      sessionStorage.removeItem(COVER_LETTER_EDITOR_STATE_KEY);
      console.log('⏰ Cover letter editor state expired, clearing');
      return null;
    }

    console.log('✅ Cover letter editor state restored from sessionStorage');
    return state;
  } catch (err) {
    console.error('❌ Failed to restore cover letter editor state:', err);
    return null;
  }
}

/**
 * Clear saved cover letter editor state (call after successful restore)
 */
export function clearCoverLetterEditorState() {
  sessionStorage.removeItem(COVER_LETTER_EDITOR_STATE_KEY);
}
